import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Package, Store } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from '../contexts/LocationContext';
import { useLoading } from '../contexts/LoadingContext';
import Header from '../components/Header';
import CategoryNav from '../components/CategoryNav';
import BannerCarousel from '../components/BannerCarousel';
import MerchantCard from '../components/MerchantCard';
import CategorySection from '../components/CategorySection';
import GymVoucherSection from '../components/GymVoucherSection';
import RecommendationStoreSection from '../components/RecommendationStoreSection';
import { HomeSkeleton } from '../components/LoadingSkeleton';
import Footer from '../components/Footer';
import SellerCTABanner from '../components/SellerCTABanner';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const { t } = useLanguage();
  const { location, isDetecting, error: locationError } = useLocation();
  const { markStepComplete } = useLoading();

  const [merchants, setMerchants] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch products grouped by category
  const fetchCategoryProducts = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API}/products/by-category`, {
        params: {
          lon: location.lon,
          lat: location.lat,
          max_pages: 3,
          products_per_category: 10
        }
      });

      if (response.data.status === 'success') {
        setCategoryProducts(response.data.data || {});
        markStepComplete('categories');
      }
    } catch (error) {
      console.error('Error fetching category products:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, [location, markStepComplete]);

  // Fetch merchants using cached endpoint
  const fetchMerchants = useCallback(async (pageNum = 1, append = false) => {
    if (!location) return;

    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Hybrid Strategy:
      // - Initial load: Fetch only 5 pages (fast)
      // - Search/Category: Fetch 50 pages (comprehensive)
      // - Load More: Fetch 50 pages (show all)
      let maxPages = 5;
      if (searchQuery || selectedCategory) {
        maxPages = 50;
      } else if (append || pageNum > 1) {
        maxPages = 50;
      }

      const params = new URLSearchParams({
        lon: location.lon,
        lat: location.lat,
        max_pages: maxPages
      });

      if (searchQuery) {
        params.append('q', searchQuery);
      }
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const endpoint = `${API}/merchants/search-cached?${params}`;

      const response = await axios.get(endpoint);

      if (response.data.status === 'success' || response.data.status === 'cached') {
        const newMerchants = response.data.data;

        if (append) {
          setMerchants(newMerchants); // Replace list with full set
        } else {
          setMerchants(newMerchants);
        }

        // Mark merchants step complete on first load
        if (pageNum === 1) {
          markStepComplete('merchants');
        }

        // Show Load More only if we are in "Initial" mode (5 pages) and got enough data
        setHasMore(maxPages < 50 && newMerchants.length >= 50);
      } else {
        if (!append) setMerchants([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
      toast.error('Failed to load merchants');
      if (!append) setMerchants([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [location, searchQuery, selectedCategory, markStepComplete]);

  useEffect(() => {
    // Prefetch definition inside useEffect to avoid dependency issues
    const prefetchMerchants = async (pages) => {
      try {
        await axios.get(`${API}/merchants/prefetch`, {
          params: {
            lon: location.lon,
            lat: location.lat,
            max_pages: pages
          }
        });
        markStepComplete('prefetch');
      } catch (error) {
        // Silent error for background task
        console.error('Prefetch error:', error);
        // Still mark as complete even if it fails
        markStepComplete('prefetch');
      }
    };

    // Mark banners as complete (BannerCarousel loads independently)
    markStepComplete('banners');

    setPage(1);
    fetchMerchants(1, false);
    fetchCategoryProducts();

    // Progressive Loading Strategy
    // Prefetch 50 pages in background after 2s to warm cache for "Load More" / Search
    const timer = setTimeout(() => {
      prefetchMerchants(50);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedCategory, fetchMerchants, fetchCategoryProducts]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPage(1);
    fetchMerchants(1, false);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearchQuery('');
    setPage(1);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMerchants(nextPage, true);
  };

  if (isDetecting) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <HomeSkeleton />
      </div>
    );
  }

  if (locationError || !location) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="home-page">
        <Header onSearch={handleSearch} />
        <main className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-[#0052CC]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Location Required</h2>
            <p className="text-gray-600 mb-6">
              Please enable location access to see merchants near you.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-full px-8 py-2"
            >
              Allow Access
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="home-page">
      <Header onSearch={handleSearch} />
      <CategoryNav
        categories={Object.keys(categoryProducts)}
      />

      {/* Hero Banner Carousel */}
      <BannerCarousel />

      {/* Seller CTA Banner */}
      <SellerCTABanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Gym Voucher Section */}
        <GymVoucherSection />

        {/* MPStore Recommendation Store (merchant whitelist) */}
        {!searchQuery && !selectedCategory && <RecommendationStoreSection />}

        {/* Category Sections */}
        {!searchQuery && !selectedCategory && (
          <div className="mb-12">
            {loadingCategories ? (
              <div className="space-y-8">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="flex gap-4 overflow-hidden">
                      {[...Array(6)].map((_, j) => (
                        <div key={j} className="flex-shrink-0 w-[180px] h-64 bg-gray-200 rounded-xl"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              Object.entries(categoryProducts).map(([categoryName, products]) => (
                <CategorySection
                  key={categoryName}
                  categoryName={categoryName}
                  products={products}
                />
              ))
            )}
          </div>
        )}

        {/* Merchants Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Store className="w-6 h-6 text-[#0052CC]" />
              {t('nearby_merchants')}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMerchants(1, false)}
              className="text-[#0052CC] hover:bg-[#0052CC]/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Content */}
          {loading ? (
            <HomeSkeleton />
          ) : merchants.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="font-medium text-gray-900 mb-1">{t('no_merchants')}</h3>
              <p className="text-sm text-gray-500">Try a different location or search term</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="merchants-list">
              {merchants.map((merchant) => (
                <MerchantCard key={merchant.alias} merchant={merchant} />
              ))}
            </div>
          )}

          {/* Load More */}
          {!loading && hasMore && merchants.length > 0 && (
            <div className="mt-8 text-center">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                className="bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-full px-8 py-3"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('load_more')
                )}
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
